// The signup verification step: six boxes that break formation and orbit while
// the code is checked, then collapse into a tick.
//
// The animation is presentation only — the VERDICT is the server's. The parent
// owns the request and reports back through `status`; this component never
// decides whether a code was right. That separation matters: an animation that
// congratulates you before the API answers is a lie, and one that runs on a
// fixed timer will contradict a slow or failed request.
//
//   status: "idle"      — waiting for digits
//           "verifying" — request in flight; boxes orbit
//           "success"   — verified; boxes collapse into the tick
//           "error"     — rejected; card shakes and clears
//
// If the orbit finishes before the server answers (a slow network), the stage
// holds a spinner rather than showing a tick it hasn't earned.

import { useCallback, useEffect, useRef, useState } from "react";

const RESEND_LABEL = (s) => `Resend in 0:${String(s).padStart(2, "0")}`;

export default function OtpVerify({
  length = 6,
  email,
  status = "idle",
  error = "",
  note = "",
  resendIn = 0,
  resending = false,
  onComplete,
  onResend,
  onBack,
}) {
  const [digits, setDigits] = useState(() => Array(length).fill(""));
  const [focused, setFocused] = useState(0);
  const [phase, setPhase] = useState("input");   // input | orbit | success | error
  const [orbitDone, setOrbitDone] = useState(false);
  const inputs = useRef([]);

  const code = digits.join("");
  const complete = code.length === length;
  const locked = phase !== "input" && phase !== "error";
  const isSuccess = phase === "success";

  const clear = useCallback(() => {
    setDigits(Array(length).fill(""));
    setPhase("input");
    setOrbitDone(false);
    setFocused(0);
    setTimeout(() => inputs.current[0]?.focus(), 30);
  }, [length]);

  // Hand the finished code up. The parent decides what happens next; all we do
  // here is start the orbit so the wait has something to look at.
  useEffect(() => {
    if (!complete || phase !== "input") return;
    inputs.current[length - 1]?.blur();
    setOrbitDone(false);
    setPhase("orbit");
    onComplete?.(code);
    // Deliberately keyed on completeness only: re-running when `onComplete`'s
    // identity changes would fire the request twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, phase]);

  // The server's answer drives the ending.
  useEffect(() => {
    if (status === "success") setPhase("success");
    if (status === "error" && phase !== "input") {
      setPhase("error");
      const t = setTimeout(clear, 820);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, phase, clear]);

  useEffect(() => {
    const t = setTimeout(() => inputs.current[0]?.focus(), 600);
    return () => clearTimeout(t);
  }, []);

  // ---- typing --------------------------------------------------------------
  function handleChange(i, raw) {
    if (locked) return;
    const val = raw.replace(/\D/g, "");
    if (!val) return;
    setDigits((prev) => {
      const next = [...prev];
      val.split("").forEach((ch, k) => { if (i + k < length) next[i + k] = ch; });
      return next;
    });
    const land = Math.min(i + val.length, length - 1);
    setFocused(land);
    inputs.current[land]?.focus();
  }

  function handleKeyDown(i, e) {
    if (locked) { e.preventDefault(); return; }
    if (e.key === "Backspace") {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        if (next[i]) next[i] = "";
        else if (i > 0) {
          next[i - 1] = "";
          setFocused(i - 1);
          inputs.current[i - 1]?.focus();
        }
        return next;
      });
    }
    if (e.key === "ArrowLeft" && i > 0) {
      setFocused(i - 1);
      inputs.current[i - 1]?.focus();
    }
    if (e.key === "ArrowRight" && i < length - 1) {
      setFocused(i + 1);
      inputs.current[i + 1]?.focus();
    }
  }

  function handlePaste(e) {
    if (locked) return;
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    const next = Array(length).fill("");
    text.split("").forEach((ch, k) => { next[k] = ch; });
    setDigits(next);
    const land = Math.min(text.length, length - 1);
    setFocused(land);
    inputs.current[land]?.focus();
  }

  // Orbit ran out before the server replied — hold, don't celebrate.
  const stalled = phase === "orbit" && orbitDone && status !== "success";

  return (
    <div className="otp-root">
      <style>{CSS}</style>

      <div className={`otp-card ${phase === "error" ? "is-shake" : ""}`} data-phase={phase}>
        <span className="otp-wash" aria-hidden="true" />

        <div className="otp-head">
          <div className="otp-head-layer" data-on={!isSuccess}>
            <h1 className="otp-title">Check your email</h1>
            <p className="otp-sub">
              We&rsquo;ve sent a {length}-digit code to
              <br />
              <strong>{email}</strong>
            </p>
          </div>
          <div className="otp-head-layer" data-on={isSuccess}>
            <h1 className="otp-title">Verified</h1>
            <p className="otp-sub">Setting up your workspace&hellip;</p>
          </div>
        </div>

        <div className="otp-stage" data-phase={phase} onPaste={handlePaste}>
          <span className="otp-guide" aria-hidden="true" />

          {digits.map((d, i) => {
            const active = focused === i && !locked;
            return (
              <div
                key={i}
                className="otp-slot"
                style={{
                  "--x": `${(i - (length - 1) / 2) * 54}px`,
                  // Spread the starting angles evenly however many boxes there are.
                  "--a0": `${(i * 360) / length}deg`,
                }}
              >
                <div
                  className="otp-orbit"
                  onAnimationEnd={(e) => {
                    if (i === 0 && e.animationName === "otpOrbit") setOrbitDone(true);
                  }}
                >
                  <label className={[
                    "otp-box",
                    d ? "is-filled" : "",
                    active ? "is-active" : "",
                  ].join(" ")}
                  >
                    <input
                      ref={(el) => { inputs.current[i] = el; }}
                      value={d}
                      onChange={(e) => handleChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      onFocus={() => setFocused(i)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={length}
                      aria-label={`Digit ${i + 1} of ${length}`}
                      disabled={locked}
                    />
                    {d && <span className="otp-digit">{d}</span>}
                    {active && !d && <span className="otp-caret" />}
                  </label>
                </div>
              </div>
            );
          })}

          {stalled && (
            <div className="otp-waiting" role="status">
              <span className="otp-spinner" aria-hidden="true" /> Verifying&hellip;
            </div>
          )}

          <div className="otp-result" aria-hidden={!isSuccess}>
            <span className="otp-ring r1" />
            <span className="otp-ring r2" />
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <span key={n} className="otp-spark" style={{ "--n": n }} />
            ))}
            <div className="otp-mark">
              <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
                <path
                  className="otp-check"
                  d="M5 12.5l4.6 4.6L19 7.6"
                  fill="none" stroke="#fff" strokeWidth="2.7"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* The server's message, verbatim — it carries the useful part
            ("2 attempts left"), which a generic string would throw away. */}
        <div className="otp-msg" role="alert" data-tone={error ? "bad" : note ? "good" : "none"}>
          {error || note || " "}
        </div>

        <div className="otp-foot">
          <p className="otp-foot-layer" data-on={!isSuccess}>
            Didn&rsquo;t get it?{" "}
            <button
              type="button"
              className="otp-link"
              disabled={resendIn > 0 || resending || locked}
              onClick={onResend}
            >
              {resending ? "Sending…" : resendIn > 0 ? RESEND_LABEL(resendIn) : "Resend code"}
            </button>
          </p>
          <p className="otp-foot-layer ok" data-on={isSuccess}>
            Verified &amp; secured
          </p>
        </div>
      </div>

      {!isSuccess && onBack && (
        <button type="button" className="otp-back" onClick={onBack}>
          Use a different email
        </button>
      )}
    </div>
  );
}

// Kept as CSS rather than utility classes: the orbit is a multi-stage keyframe
// with per-box angle offsets, which Tailwind can't express. Variables are wired
// to the same palette as the rest of the app, and every colour has a dark-mode
// counterpart — this panel sits next to a theme toggle.
const CSS = `
.otp-root{
  --accent:#6366f1;          /* brand-500 */
  --accent-soft:rgba(99,102,241,.12);
  --ok:#10b981;              /* emerald-500 */
  --card:#ffffff;
  --card-edge:rgba(15,23,42,.10);
  --box:#f8fafc;
  --box-edge:rgba(15,23,42,.12);
  --ink:#0f172a;
  --dim:#64748b;
  --shadow:0 24px 50px -28px rgba(15,23,42,.35);

  position:relative;display:flex;flex-direction:column;align-items:center;gap:16px;
}
.dark .otp-root{
  --accent:#818cf8;          /* brand-400 reads better on dark */
  --accent-soft:rgba(129,140,248,.16);
  --ok:#34d399;
  --card:#141a2e;            /* ink-900 */
  --card-edge:rgba(255,255,255,.10);
  --box:rgba(255,255,255,.04);
  --box-edge:rgba(255,255,255,.12);
  --ink:#ffffff;
  --dim:rgba(255,255,255,.55);
  --shadow:0 30px 60px -30px rgba(0,0,0,.8);
}

.otp-card{
  position:relative;width:100%;max-width:420px;
  padding:26px 24px 22px;border-radius:24px;
  border:1px solid var(--card-edge);background:var(--card);
  box-shadow:var(--shadow);text-align:center;overflow:hidden;
}

/* success wash */
.otp-wash{
  position:absolute;inset:0;pointer-events:none;opacity:0;
  background:radial-gradient(circle at 50% 60%,rgba(16,185,129,.16),transparent 68%);
  transition:opacity .9s ease;
}
.otp-card[data-phase="success"] .otp-wash{opacity:1}

/* headings crossfade */
.otp-head{position:relative;height:86px}
.otp-head-layer{
  position:absolute;inset:0;opacity:0;transform:translateY(8px);pointer-events:none;
  transition:opacity .5s ease,transform .5s cubic-bezier(.2,.75,.25,1);
}
.otp-head-layer[data-on="true"]{opacity:1;transform:none}
.otp-title{margin:0 0 6px;font-size:21px;font-weight:700;letter-spacing:-.02em;color:var(--ink)}
.otp-sub{margin:0;font-size:13px;line-height:1.6;color:var(--dim)}
.otp-sub strong{color:var(--ink);font-weight:600}

/* stage */
.otp-stage{position:relative;height:170px;margin:2px 0}
.otp-guide{
  position:absolute;left:50%;top:50%;width:140px;height:140px;margin:-70px 0 0 -70px;
  border-radius:50%;border:1px dashed var(--box-edge);
  opacity:0;transform:scale(.7);transition:opacity .4s ease,transform .5s ease;
}
.otp-stage[data-phase="orbit"] .otp-guide{opacity:1;transform:scale(1)}

.otp-slot{
  position:absolute;left:50%;top:50%;width:46px;height:56px;margin:-28px 0 0 -23px;
  transform:translateX(var(--x));
  transition:transform .42s cubic-bezier(.6,0,.2,1);
}
.otp-stage[data-phase="orbit"] .otp-slot,
.otp-stage[data-phase="success"] .otp-slot{transform:translateX(0)}

.otp-orbit{width:100%;height:100%;transform-origin:center}
.otp-stage[data-phase="orbit"] .otp-orbit{
  animation:otpOrbit 1.6s cubic-bezier(.5,.02,.35,1) .28s both;
}
@keyframes otpOrbit{
  0%   {transform:rotate(0deg) translateY(0) scale(1)}
  16%  {transform:rotate(calc(var(--a0) + 70deg)) translateY(-64px) scale(.94)}
  55%  {transform:rotate(calc(var(--a0) + 420deg)) translateY(-64px) scale(.94)}
  84%  {transform:rotate(calc(var(--a0) + 665deg)) translateY(-24px) scale(.76)}
  100% {transform:rotate(calc(var(--a0) + 720deg)) translateY(0) scale(.4);opacity:0}
}
.otp-stage[data-phase="success"] .otp-orbit{opacity:0;transform:scale(.4)}

/* box */
.otp-box{
  position:relative;width:100%;height:100%;
  border-radius:14px;background:var(--box);border:1px solid var(--box-edge);
  display:grid;place-items:center;cursor:text;
  transition:border-color .25s ease,box-shadow .25s ease,transform .25s cubic-bezier(.2,.8,.2,1),background .25s ease;
}
.otp-box input{
  position:absolute;inset:0;width:100%;height:100%;
  border:0;outline:0;background:transparent;color:transparent;caret-color:transparent;
  text-align:center;font-size:22px;border-radius:inherit;
}
.otp-box.is-active{
  border-color:var(--accent);transform:translateY(-2px);
  box-shadow:0 0 0 3px var(--accent-soft),0 8px 20px -10px var(--accent);
}
.otp-box.is-filled{border-color:var(--accent);box-shadow:inset 0 0 0 1px var(--accent-soft)}
.otp-stage[data-phase="orbit"] .otp-box{
  border-color:var(--accent);box-shadow:0 0 22px -6px var(--accent);
}
.otp-card.is-shake .otp-box{border-color:#f43f5e;box-shadow:0 0 20px -8px rgba(244,63,94,.7)}

.otp-digit{
  font-size:22px;font-weight:700;color:var(--ink);
  font-variant-numeric:tabular-nums;pointer-events:none;
  animation:otpPop .26s cubic-bezier(.2,.9,.3,1.5) both;
}
@keyframes otpPop{from{opacity:0;transform:scale(.5) translateY(6px)}to{opacity:1;transform:none}}

.otp-caret{
  width:2px;height:22px;background:var(--accent);border-radius:2px;pointer-events:none;
  animation:otpBlink 1.05s steps(1,end) infinite;
}
@keyframes otpBlink{0%,49%{opacity:1}50%,100%{opacity:0}}

/* holding pattern when the request outlasts the animation */
.otp-waiting{
  position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);
  display:flex;align-items:center;justify-content:center;gap:8px;
  font-size:13px;color:var(--dim);
}
.otp-spinner{
  width:15px;height:15px;border-radius:50%;
  border:2px solid var(--accent-soft);border-top-color:var(--accent);
  animation:otpSpin .7s linear infinite;
}
@keyframes otpSpin{to{transform:rotate(360deg)}}

/* success mark */
.otp-result{position:absolute;left:50%;top:50%;width:0;height:0;display:grid;place-items:center;pointer-events:none}
.otp-mark{
  position:absolute;left:50%;top:50%;width:68px;height:68px;margin:-34px 0 0 -34px;
  border-radius:20px;background:var(--ok);display:grid;place-items:center;
  box-shadow:0 0 30px -6px var(--ok);opacity:0;transform:scale(.35) rotate(-45deg);
}
.otp-stage[data-phase="success"] .otp-mark{animation:otpMarkIn .6s cubic-bezier(.2,.85,.3,1.35) both}
@keyframes otpMarkIn{
  0%{opacity:0;transform:scale(.35) rotate(-45deg)}
  100%{opacity:1;transform:scale(1) rotate(0)}
}
.otp-check{stroke-dasharray:26;stroke-dashoffset:26}
.otp-stage[data-phase="success"] .otp-check{animation:otpDraw .4s cubic-bezier(.6,0,.3,1) .32s forwards}
@keyframes otpDraw{to{stroke-dashoffset:0}}

.otp-ring{
  position:absolute;left:50%;top:50%;width:88px;height:88px;margin:-44px 0 0 -44px;
  border-radius:50%;border:1.5px solid var(--ok);opacity:0;
}
.otp-stage[data-phase="success"] .otp-ring{animation:otpRing 1.5s cubic-bezier(.2,.7,.3,1) infinite}
.otp-stage[data-phase="success"] .otp-ring.r2{animation-delay:.45s}
@keyframes otpRing{0%{transform:scale(.75);opacity:.8}100%{transform:scale(2);opacity:0}}

.otp-spark{
  position:absolute;left:50%;top:50%;width:5px;height:5px;margin:-2.5px 0 0 -2.5px;
  border-radius:2px;background:var(--ok);opacity:0;
}
.otp-stage[data-phase="success"] .otp-spark{
  animation:otpSpark .8s cubic-bezier(.2,.8,.3,1) both;
  animation-delay:calc(.3s + var(--n) * .045s);
  transform:rotate(calc(var(--n) * 60deg)) translateY(-44px);
}
@keyframes otpSpark{
  0%{opacity:0;transform:rotate(calc(var(--n) * 60deg)) translateY(-18px) scale(.4)}
  45%{opacity:1}
  100%{opacity:0;transform:rotate(calc(var(--n) * 60deg)) translateY(-62px) scale(.5)}
}

/* message line — fixed height so the card doesn't jump as it appears */
.otp-msg{
  min-height:20px;font-size:12.5px;line-height:1.5;margin:2px 0 8px;
  transition:color .2s ease;color:transparent;
}
.otp-msg[data-tone="bad"]{color:#e11d48;font-weight:500}
.dark .otp-msg[data-tone="bad"]{color:#fb7185}
.otp-msg[data-tone="good"]{color:var(--ok);font-weight:500}

/* footer */
.otp-foot{position:relative;height:22px}
.otp-foot-layer{
  position:absolute;inset:0;margin:0;font-size:12.5px;color:var(--dim);
  opacity:0;transform:translateY(6px);pointer-events:none;
  transition:opacity .45s ease .1s,transform .45s ease .1s;
}
.otp-foot-layer[data-on="true"]{opacity:1;transform:none;pointer-events:auto}
.otp-foot-layer.ok{color:var(--ok);font-weight:600}
.otp-link{
  background:none;border:0;padding:0;font:inherit;font-weight:600;
  color:var(--accent);cursor:pointer;
}
.otp-link:disabled{color:var(--dim);cursor:default;font-weight:500}

.otp-back{
  background:none;border:0;padding:0;font-size:12.5px;font-weight:500;
  color:var(--dim);cursor:pointer;text-decoration:underline;text-underline-offset:3px;
}
.otp-back:hover{color:var(--ink)}

.is-shake{animation:otpShake .5s cubic-bezier(.36,.07,.19,.97) both}
@keyframes otpShake{
  10%,90%{transform:translateX(-2px)}
  20%,80%{transform:translateX(4px)}
  30%,50%,70%{transform:translateX(-7px)}
  40%,60%{transform:translateX(7px)}
}

.otp-box input:focus-visible{box-shadow:0 0 0 2px var(--accent)}

@media (max-width:400px){
  .otp-slot{width:40px;height:50px;margin:-25px 0 0 -20px}
  .otp-digit,.otp-box input{font-size:19px}
}

/* Motion is decoration here — the flow must still work without it. */
@media (prefers-reduced-motion:reduce){
  .otp-root *,.otp-root *::before,.otp-root *::after{
    animation-duration:.01ms !important;animation-iteration-count:1 !important;
    transition-duration:.01ms !important;
  }
}
`;
